import { useAppContext } from 'src/components/app-context';
import { useContext, useEffect, useState } from 'react';
import matchRoute from 'src/routes/match-route';
import { useLocation } from 'react-router';
import { routeConfig } from 'src/routes/config';
import { useDispatch, useSelector } from 'react-redux';
import { getUrlHostPrefix } from 'src/routes';
import { useLocationContext } from 'src/components/location-context';
import type { AppState } from 'reducers';
import { ExperimentContext } from 'src/components/experiment';
import { useAdServiceContext } from 'src/components/ads/services/context';

export function useRouteLoaded(data: unknown, isFetching?: boolean) {
  const location = useLocation();
  const context = useAppContext();
  const { market } = context;
  const { currentLocation, previousLocation } = useLocationContext();
  const [isNewLocation, setIsNewLocation] = useState(false);
  const dispatch = useDispatch();
  const hrefLang = useSelector((state: AppState) => state.globals.hrefLang);
  const experiments = useContext(ExperimentContext);
  const adServiceContext = useAdServiceContext();

  useEffect(() => {
    setIsNewLocation(true);
  }, [currentLocation]);

  useEffect(() => {
    if (!window?.dataLayer || !data || !isNewLocation || isFetching) {
      return;
    }
    const route = matchRoute({
      useClassicUrls: market.useClassicUrls,
      useHashForProductTabs: market.useHashForProductTabs,
      routeConfig,
      localizedRoutes: market.routes,
      location,
      marketCode: market.code,
      experiments: experiments.experiments,
    });

    const meta =
      route?.metaTags?.(
        {
          data,
          routeName: route.name,
          hrefLang,
          ...route.variables,
        },
        context
      ) ?? {};

    const routeData = {
      page: route && route.name,
      meta,
      section:
        (route &&
          route.match &&
          route.match.params &&
          route.match.params.section) ||
        undefined,
      previousLocation: getUrlHostPrefix() + previousLocation,
      currentLocation: getUrlHostPrefix() + currentLocation,
    };

    // Since Google Tag Manager does a recursive merge of data in its data layer
    // we do a reset of our route data here to avoid merges of arrays and objects
    // when navigating between routes with the same route name
    const dataReset = {};

    Object.keys(data).forEach(key => {
      dataReset[key] = undefined;
    });

    window.dataLayer.push({
      event: 'resetRouteDataLayer',
      ...routeData,
      ...dataReset,
    });

    window.dataLayer.push({
      event: 'routeLoaded',
      ...routeData,
      ...data,
    });

    const action = {
      type: 'ROUTE_LOADED',
      location,
      route,
      data,
    };

    dispatch(action);
    setIsNewLocation(false);

    if (adServiceContext) {
      adServiceContext.update();
    }
  }, [isNewLocation, data, isFetching]);
}
